'use strict';
/* ==========================================================================
   levels/check.js — step "check" (orientation any): SEED CHECK (DESIGN.md §5.3). Spot the mistake on a garden-club poster.

   An attempt = 6 posters from the pool `check` (content/bank.check.json through Cross.pool → core/bank.js: draw.n = 6,
   draw.unique = "cheat" so no cheat twice, draw.require = ["legit"] so exactly one honest poster), served easiest first
   (Cross.byLevel). Every poster is built by the model: Cross.poster(item) → the honest square, the boxes AS DRAWN (a
   wrongBox cheat changes one), every claim judged by Cross.claim, and the answer. Nothing here decides what is true:
   the poster's `answer` and each claim's `verdict` do; the "checks out" notes are written from the model's numbers.

   A poster hangs on the greenhouse wall from a nail on garden twine: a kraft title strip ("GARDEN CLUB · SEED COLOR
   CROSS"), the two parents (phenotype picture + genotype tiles), the filled seed-tray Punnett square (Plate.tray) and
   2–3 claim lines (A, B, C).
   Per poster:  drop (it swings in on its twine)
             →  spot: tap a box to see how it was built ("Y from the top, y from the side": free, never costs a star),
                "Mark this box wrong" to accuse it; tap a claim to accuse it; or "All correct". A wrong accusation gets a
                pencil tick and why it's fine (the box's build, or the claim's numbers from the square); 3 misses and
                Mr. Mendoza circles the mistake himself (or calls the poster all correct).
             →  q: the item's question, its 3 options shuffled by the bank; a wrong pick is crossed out with its `why`
             →  fix: Mr. Mendoza's red pencil: a wrong box is ringed, its tiles struck, and the honest tiles slide in
                from the parents' edges; a wrong claim is struck through; his note (the item's `fix`) on a slip. An
                all-correct poster gets green ticks and an ALL CORRECT stamp.
   Stars: right spot first try 1 + right option first try 1 (legit: "All correct" first try 1 + option 1). of = 12,
   pass = pool.passStars (8). Game.progress.scores.check = {best, last, of}; first answers under
   Game.progress.answers['check/<key>/spot'] and ['check/<key>']; Bank.finish at the end; the stamp (Game.complete)
   through "Collect your stamp" on a pass (a pass that leaves without collecting keeps it). Resume: Game.progress.bank.
   check.run = {i, ev, res} (the current poster's taps and picks are replayed after a reload).

   Portrait phone (Dock.active): the poster is the picture on the stage; the claims, the boxes (how it was built), "Mark
   this box wrong", "All correct", the options and Mr. Mendoza's card are dock widgets.
   Keys: 1–4 = look at box n (numbered on the tray), X = mark that box wrong, A–C = claim, 0 = All correct,
   1–3 = option, Enter / Space = next / start.

   QA hooks: sc.phase ('intro' | 'drop' | 'spot' | 'q' | 'fix' | 'end'), sc.poster (the Cross.poster result), sc.inst,
   sc.answer() ({kind: 'box' | 'claim' | 'legit', index}), sc.how(i) (free look), sc.tapBox(i), sc.tapClaim(i), sc.legit(),
   sc.choose(i), sc.next(), sc.begin(fresh), sc.collect(), sc.done(), sc.finishStub() (= done); sc.bi, sc.n, sc.seed,
   sc.attempt, sc.resumed, sc.results, sc.stars, sc.misses, sc.picks, sc.sel; Levels.check.solution ({kind, index,
   option}), Levels.check.zones() ({boxes, claims} tap rects on the canvas), Levels.check.preview(id) (swap the current
   poster for a pool item: screenshots of the longest claims).
   ========================================================================== */
(function () {
  const STEP = 'check', TITLE = 'Seed Check';
  const MAX_MISSES = 3, DROP = 0.8, NEXT_GUARD = 0.3;
  const pool = () => Bank.pool(STEP) || Bank.load(Cross.pool(Game.content.bank_check));
  const PL = () => window.Plate;
  const SLAB = "'Alfa Slab One'", SERIF = "'Playfair Display'", HAND = "'Caveat Brush'";
  const PENCIL = '#5b6475';
  const BOXN = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
  const LETTER = ['A', 'B', 'C', 'D'];
  const CROSS_TITLE = { seedColour: 'SEED COLOR', seedShape: 'SEED SHAPE', flowerColour: 'FLOWER COLOR', podShape: 'POD SHAPE', stemLength: 'STEM LENGTH', catEar: 'MADE-UP CAT EAR', eyeModel: 'EYE COLOR' };
  const SHORT = { legit: 'all correct', wrongBox: 'a box filled in wrong', genoAsPheno: 'a genotype % read as a phenotype %', countNotChance: 'a chance read as an exact count',
    blend: 'blending (one gene doesn\'t blend)', dominantCommon: '"dominant" read as "common"', recessiveGone: 'a hidden allele called gone' };
  const Rn = v => Math.max(1, Math.round(v));
  const R10 = v => Math.max(10, Math.round(v / 10) * 10);
  const measure = (s, font) => { g.save(); g.font = font; const w = g.measureText(s).width; g.restore(); return w; };
  const lerpN = (a, b, k) => a + (b - a) * k;

  /* ---------------- the model's words ---------------- */
  const build = (P, i) => { const b = P.square.boxes[i]; return `${b.top} from the top, ${b.side} from the side`; };
  /* why a TRUE claim checks out, from the square's numbers (never from the author's text) */
  function checksOut(P, c) {
    const v = c.verdict, sq = P.square, T_ = Cross.TRAITS[P.trait];
    const count = spec => sq.boxes.filter(b => (spec.kind === 'geno' ? b.genotype === spec.value : b.phenotype === spec.value)).length;
    if (v.kind === 'pct') { const k = count(v.spec); return `${k} of 4 boxes ${k === 1 ? 'is' : 'are'} ${v.spec.value}: ${v.computed}%.`; }
    if (v.kind === 'all') return `All 4 boxes are ${v.spec.value}.`;
    if (v.kind === 'none') return `No box is ${v.spec.value}.`;
    if (v.kind === 'definition') return 'Each box is one way the parents\' letters can pair up.';
    if (v.kind === 'dominance') return `One ${T_.letter} is enough to show ${T_.dominant}.`;
    if (v.kind === 'parents') return `${sq.p1} and ${sq.p2} both show ${v.value}.`;
    return 'The square backs it up.';
  }
  const shortTitle = P => `${CROSS_TITLE[P.trait] || String(Cross.TRAITS[P.trait].gene).toUpperCase()} CROSS`;

  /* ---------------- layout (mode: 'm' mouse / desktop, 't' touch landscape, 'd' the portrait stage picture) ---------------- */
  function posterLay(P, mode, fixText) {
    const d = mode === 'd', t = mode === 't', Pl = PL();
    const PX = d ? 40 : 66, PW = d ? 1840 : 1246, PY = d ? 132 : t ? 224 : 188, PH = (d ? 1062 : 1066) - PY;
    const cx = PX + PW / 2, nail = { x: cx, y: PY - (d ? 40 : 34) };
    const title = 'GARDEN CLUB · ' + shortTitle(P);
    let ts = d ? 60 : 44;
    while (ts > 24 && Pl.woodWidth(title, ts) > PW - 150) ts -= 2;
    const titleY = PY + (d ? 62 : 56);
    const parY = PY + (d ? 170 : 146), parTile = d ? 60 : 48, parPic = d ? 40 : 32, parCap = d ? 34 : Math.round(Math.min(34, UI.text(26)));
    const cell = d ? 196 : t ? 160 : 172;
    const regTop = parY + parTile * 0.7 + parCap * 1.3 + (d ? 16 : 10), regBot = PY + PH - (d ? 24 : 26);
    const tx = PX + (d ? 46 : 34) + 1.63 * cell;
    const ty = Math.max(regTop + 1.63 * cell, (regTop + 1.63 * cell + regBot - 1.16 * cell) / 2);
    const tray = Pl.trayLayout(tx, ty, cell);
    const colX = tx + 1.16 * cell + (d ? 56 : 40), colR = PX + PW - (d ? 44 : 32), colW = colR - colX;
    const colTop = regTop, colBot = regBot;
    // the claims + the fix slip, shrunk until they fit the column
    const base = d ? 50 : Math.round(UI.text(32)), min = d ? 34 : 22;
    let C = null;
    for (let fs = base; fs >= min; fs -= 2) {
      const lh = fs * 1.2, bw = Math.round(fs * 1.25), pad = Math.round(fs * 0.42), rowMin = d ? 0 : UI.tap(64);
      const hs = Math.round(fs * 1.05), head = hs * 1.5;
      const font = `${fs}px ${SERIF}`;
      let y = colTop + head;
      const rows = P.claims.map((c, i) => {
        const lines = UI._wrap(c.text, colW - bw - 26, font);
        const h = Math.max(rowMin, lines.length * lh + pad * 2);
        const r = { i, top: y, h, cy: y + h / 2, lines, lineW: lines.map(l => measure(l, font)) };
        y += h + Math.round(fs * 0.25);
        return r;
      });
      const fxs = Math.round(fs * 1.08), fxFont = `${fxs}px ${HAND}`, fxLh = fxs * 1.1;
      const fxLines = UI._wrap(fixText || '', colW - 60, fxFont);
      const fixTop = y + Math.round(fs * 0.4), fixH = fxLines.length * fxLh + fxs * 1.3 + 24;
      C = { fs, lh, bw, pad, font, hs, head, rows, fxs, fxFont, fxLh, fxLines, fixTop, fixH };
      if (fixTop + fixH <= colBot) break;
    }
    return { mode, PX, PW, PY, PH, cx, nail, title, ts, titleY, parY, parTile, parPic, parCap, cell, tray, colX, colW, colTop, colBot, ...C };
  }

  class Check extends Scene {
    get pool() { return pool(); }
    get n() { return this.instances ? this.instances.length : (this.pool.draw.n || 6); }
    get of() { return this.n * 2; }
    get passStars() { const j = this.pool.json || {}; return Math.min(this.of, j.passStars || Math.ceil(this.of * 2 / 3)); }
    get inst() { return this.instances ? this.instances[this.bi] : null; }
    get poster() { return this.P || null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() {
      const P = this.P, q = this.inst;
      if (!P) return null;
      const a = P.answer;
      return { kind: a.kind === 'none' ? 'legit' : a.kind, index: a.index, option: q.answerIndex, optionText: q.options[q.answerIndex] };
    }
    answer() { const s = this.solution; return s ? { kind: s.kind, index: s.index } : null; }

    enter() {
      this.phase = 'intro'; this.instances = null; this.P = null; this.bi = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      this.sfx('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }
    back() { Game.replace(new Levels.map()); }

    /* ---- the attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Bank.attempt(STEP, pool(), { fresh });
      this.instances = Cross.byLevel(a.instances);                                  // easiest first (stable)
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startPoster(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.instances.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.boardAt = Game.time - DROP - 2; this.phase = 'spot';
        this.quiet = true;
        for (const [k, v] of run.ev || []) {
          if (k === 'h') this.how(v); else if (k === 'b') this.tapBox(v); else if (k === 'c') this.tapClaim(v);
          else if (k === 'l') this.legit(); else if (k === 'o') this.choose(v);
        }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[check] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.bi = i; this.P = Cross.poster(this.instances[i].item); this.lays.clear();
      this.ev = []; this.misses = 0; this.marks = { box: {}, claim: {} }; this.sel = -1; this.selAt = null;
      this.found = null; this.revealed = false; this.picks = []; this.say = null; this.spotStar = 0;
      this.boardAt = Game.time; this.foundAt = this.qAt = this.fixAt = this.pickedAt = null; this.popped = false;
      this._sc = this._qc = this._fc = null;
    }
    startPoster(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.sfx(Audio.has && Audio.has('page') ? 'page' : 'whoosh', { gain: 0.4 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.bi, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, o) { if (!this.quiet && (!Audio.has || Audio.has(name))) Audio.play(name, o); }
    alt(name, fallback, o) { this.sfx(Audio.has && Audio.has(name) ? name : fallback, o); }
    spotting() { if (this.phase === 'drop') this.phase = 'spot'; return this.phase === 'spot' && !!this.P; }
    spotKey() { return STEP + '/' + this.inst.key + '/spot'; }

    /* ---- a box: how it was built (free) ---- */
    how(i) {
      if (!this.spotting() || !(i >= 0 && i < 4)) return false;
      this.ev.push(['h', i]);
      this.sel = i; this.selAt = Game.time; this._sc = null;
      this.sfx('pop', { gain: 0.3, rate: 1.2 });
      this.save();
      return true;
    }
    /* ---- accuse a box / a claim, or call it all correct ---- */
    tapBox(i) {
      if (!this.spotting() || !(i >= 0 && i < 4) || this.marks.box[i]) return false;
      const P = this.P;
      this.ev.push(['b', i]);
      Game.progress.answer(this.spotKey(), `the ${BOXN[i]} box (${P.shown[i].genotype})`);   // first try only
      if (P.answer.kind === 'box' && P.answer.index === i) { this.foundIt(false); this.save(); return true; }
      this.marks.box[i] = { at: Game.time };
      const b = P.square.boxes[i];
      this.miss(`That box is right: ${build(P, i)} = ${b.genotype}.`);
      this.save();
      return true;
    }
    tapClaim(i) {
      const P = this.P;
      if (!this.spotting() || !(i >= 0 && i < P.claims.length) || this.marks.claim[i]) return false;
      this.ev.push(['c', i]);
      Game.progress.answer(this.spotKey(), P.claims[i].text);
      if (P.answer.kind === 'claim' && P.answer.index === i) { this.foundIt(false); this.save(); return true; }
      this.marks.claim[i] = { at: Game.time };
      this.miss(`Claim ${LETTER[i]} checks out: ${checksOut(P, P.claims[i])}`);
      this.save();
      return true;
    }
    legit() {
      if (!this.spotting()) return false;
      const P = this.P;
      this.ev.push(['l']);
      Game.progress.answer(this.spotKey(), 'All correct');
      if (P.answer.kind === 'none') { this.foundIt(false); this.sfx('ding', { gain: 0.6 }); this.save(); return true; }
      this.miss('Not all correct: one box or one claim is wrong. Check each box\'s letters and each claim against the square.');
      this.save();
      return true;
    }
    miss(text) {
      this.misses++; this.say = { text, at: Game.time }; this._sc = null;
      this.sfx('thunk', { gain: 0.4, rate: 0.9 });
      if (this.misses >= MAX_MISSES) {
        this.say = { text: this.P.answer.kind === 'none' ? 'Everything on this poster checks out: it\'s all correct.' : 'Mr. Mendoza circles it for you.', at: Game.time };
        this.foundIt(true);
      }
    }
    foundIt(revealed) {
      const a = this.P.answer;
      this.found = { kind: a.kind === 'none' ? 'legit' : a.kind, index: a.index };
      this.revealed = revealed; this.foundAt = Game.time; this.qAt = Game.time + 0.5;
      this.spotStar = !revealed && this.misses === 0 ? 1 : 0;
      if (!revealed) this.say = null;
      this.phase = 'q'; this._qc = null;
      if (a.kind !== 'none') this.sfx('scribble', { gain: 0.4 });
    }

    /* ---- what's the fix? ---- */
    choose(i) {
      const q = this.inst;
      if (this.phase !== 'q' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['o', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + q.key, q.options[i]);   // first try only
      this.picks.push(i); this.pickedAt = Game.time; this._qc = null;
      if (Bank.check(q, i)) { this.sfx('ding', { gain: 0.6 }); this.toFix(this.spotStar, this.picks.length === 1 ? 1 : 0); }
      else this.sfx('thunk', { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toFix(spotStar, fixStar) {
      const P = this.P;
      this.phase = 'fix'; this.fixAt = Game.time; this._fc = null; this.popped = false;
      this.results[this.bi] = { id: P.id, key: this.inst.key, cheat: P.cheat, trait: P.trait, legit: P.answer.kind === 'none', misses: this.misses,
        revealed: this.revealed, wrongPicks: this.picks.length - 1, spotStar, fixStar, stars: spotStar + fixStar };
      this.sfx('scribble', { gain: 0.45 });
    }
    next() {
      if (this.phase !== 'fix' || Game.time - this.fixAt < NEXT_GUARD) return false;
      if (this.bi < this.instances.length - 1) this.startPoster(this.bi + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Bank.finish(STEP);                                                            // the next attempt draws new posters
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    /* a pass that leaves without "Collect your stamp" still keeps its stamp */
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.instances) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    finishStub() { this.done(); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new posters)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new posters)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => this.back() }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.boardAt > DROP + 0.1) this.phase = 'spot';
      // the honest tiles land in the fixed box
      if (this.phase === 'fix' && !this.popped && this.P && this.P.answer.kind === 'box' && Game.time - this.fixAt > 1.95) { this.popped = true; this.alt('pod_pop', 'pop', { gain: 0.5 }); }
    }
    keyDown(k) {
      const n = parseInt(k, 10), kl = String(k).toLowerCase();
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'spot') {
        if (n >= 1 && n <= 4) this.how(n - 1);
        else if (n === 0) this.legit();
        else if (kl === 'x' && this.sel >= 0) this.tapBox(this.sel);
        else if (['a', 'b', 'c', 'd'].includes(kl)) this.tapClaim('abcd'.indexOf(kl));
      }
      else if (this.phase === 'q') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'fix') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const mode = docked ? 'd' : UI.touch() ? 't' : 'm';
      const key = mode + '|' + Math.round(UI.tap(64)) + '|' + Math.round(UI.text(32)) + '|' + this.bi + '|' + this.P.id + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { L = posterLay(this.P, mode, this.inst.explain || ''); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.instances || this.phase === 'intro' || this.phase === 'end') return '';
      return `poster ${this.bi + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    ground() {
      const Pl = PL(), C = Pl.C;
      Pl.backdrop(this.enteredAt);
      // the greenhouse wall: whitewashed boards behind the glass
      g.save(); g.globalAlpha = 0.18; g.fillStyle = C.glass; g.fillRect(34, 34, Game.W - 68, Game.H - 68); g.restore();
    }
    draw() {
      const docked = UI.docked();
      this.ground();
      UI.hud(TITLE, this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked);
      this.drawPoster(L, docked);
      if (docked) return this.drawDock();
      // Mr. Mendoza under the panel when there's room (desktop / landscape; last frame's panel bottom)
      const room = 1080 - (this.panelBottom || 1080) - 30;
      if (room >= 470) this.drawMendoza(1640, 1080, 480);
      if (this.phase === 'drop' || this.phase === 'spot') this.drawSpot();
      else if (this.phase === 'q') this.drawQ();
      else this.drawFix();
    }

    /* ---- the poster: twine + nail, the paper, the title strip, the parents, the tray, the claims, every mark ---- */
    drawPoster(L, docked) {
      const Pl = PL(), C = Pl.C, P = this.P, bi = this.bi, t0 = this.boardAt;
      const k = clamp((T - t0) / DROP), after = Math.max(0, T - t0 - DROP);
      const dy = -(1 - E.outCubic(k)) * 1150;
      const swing = (1 - k) * -0.07 + (k >= 1 ? 0.018 * Math.exp(-after * 2.6) * Math.sin(after * 7) : 0);
      const spot = this.phase === 'spot' || this.phase === 'drop', live = !docked && this.phase === 'spot' && k >= 1;
      // the nail and the twine stay on the wall
      g.save(); g.translate(0, dy);
      g.translate(L.nail.x, L.nail.y); g.rotate(swing); g.translate(-L.nail.x, -L.nail.y);
      const pinL = [L.PX + 90, L.PY + 16], pinR = [L.PX + L.PW - 90, L.PY + 16];
      Pl.twine(pinL[0], pinL[1], L.nail.x, L.nail.y, { sag: 8, seed: 41, w: 3 });
      Pl.twine(L.nail.x, L.nail.y, pinR[0], pinR[1], { sag: 8, seed: 42, w: 3 });
      // the paper
      paperRect(L.cx, L.PY + L.PH / 2, Rn(L.PW), Rn(L.PH), C.cream, { seed: 900 + (bi % 3), depth: 1.8, boil: false });
      if (IMG.tex_paper) texScrap('tex_paper', L.cx, L.PY + L.PH / 2, Rn(L.PW - 10), Rn(L.PH - 10), { seed: 905, depth: 0, torn: false, alpha: 0.25 });
      g.save(); g.strokeStyle = 'rgba(80,60,35,0.5)'; g.lineWidth = 2.5; g.strokeRect(L.PX + 18, L.PY + 18, L.PW - 36, L.PH - 36);
      g.lineWidth = 1; g.strokeRect(L.PX + 26, L.PY + 26, L.PW - 52, L.PH - 52); g.restore();
      for (const [x, y] of [pinL, pinR]) { g.save(); g.fillStyle = C.woodDark; g.beginPath(); g.arc(x, y, 7, 0, TAU); g.fill(); g.restore(); }
      // the title strip
      Pl.label(L.title, L.cx, L.titleY, L.ts, { seed: 910 + bi, rot: -0.006 });
      // the parents
      this.drawParents(L);
      // the tray (as drawn), the build lines of the box being looked at
      this.drawTray(L, docked, live, spot);
      // the claims
      this.drawClaims(L, docked, live, spot);
      // the circled mistake (the student's or Mr. Mendoza's)
      if ((this.phase === 'q' || this.phase === 'fix') && this.found && this.found.kind !== 'legit') this.circleFound(L);
      if (this.phase === 'fix') this.drawMarks(L);
      g.restore();
      // the nail on top
      g.save(); g.fillStyle = '#4a4038'; g.beginPath(); g.arc(L.nail.x, L.nail.y, docked ? 13 : 10, 0, TAU); g.fill();
      g.fillStyle = 'rgba(255,255,255,0.35)'; g.beginPath(); g.arc(L.nail.x - 3, L.nail.y - 3, 3.5, 0, TAU); g.fill(); g.restore();
    }
    drawParents(L) {
      const Pl = PL(), C = Pl.C, P = this.P, sq = P.square, s = L.parTile, y = L.parY;
      const xs = [L.cx - L.PW * 0.26, L.cx + L.PW * 0.26];
      [sq.p1, sq.p2].forEach((gt, k) => {
        const x = xs[k], word = Cross.phenotype(P.trait, gt);
        Pl.pheno(P.trait, word, x - s * 1.75, y, L.parPic, { seed: 920 + k });
        Pl.tile(gt[0], x - s * 0.1, y, s, { seed: 924 + k * 2, rot: -0.03 });
        Pl.tile(gt[1], x + s * 1.0, y, s, { seed: 925 + k * 2, rot: 0.03 });
        hand(`Parent ${k + 1} (${word}) · ${k ? 'down the side' : 'along the top'}`, x, y + s * 0.62 + L.parCap * 0.75, L.parCap, -1e6, { color: PENCIL, seed: 928 + k });
      });
      g.save(); g.font = `${Math.round(s * 1.1)}px ${SLAB}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('×', L.cx, y); g.restore();
    }
    trayBoxes(L) {
      const P = this.P;
      const boxes = P.shown.map(b => b.genotype);
      if (this.phase !== 'fix' || P.answer.kind !== 'box') return { boxes, slide: -1 };
      const at = this.fixAt + 0.2, i = P.answer.index, e = T - at;
      if (e < 0.9) return { boxes, slide: -1, fade: e > 0.7 ? clamp((e - 0.7) / 0.2) : 0 };
      if (e < 1.75) { boxes[i] = null; return { boxes, slide: clamp((e - 1.1) / 0.6) }; }
      boxes[i] = P.square.boxes[i].genotype;
      return { boxes, slide: -1 };
    }
    drawTray(L, docked, live, spot) {
      const Pl = PL(), C = Pl.C, P = this.P, sq = P.square, bi = this.bi, lz = L.tray, cell = L.cell;
      const legitFix = this.phase === 'fix' && P.answer.kind === 'none';
      const tb = this.trayBoxes(L);
      const hi = spot && this.sel >= 0 ? this.sel : -1;
      Pl.tray(lz.x, lz.y, cell, { top: sq.top, side: sq.side, boxes: tb.boxes, trait: P.trait, hi, seed: 31 + bi * 3, ok: legitFix && T > this.fixAt + 0.4 ? [0, 1, 2, 3] : [] });
      // the wrong tiles fade out (a cream wash over them) before the honest ones slide in
      if (tb.fade > 0) { const b = lz.boxes[P.answer.index]; g.save(); g.globalAlpha *= tb.fade * 0.85; g.fillStyle = C.label; g.fillRect(b.x - cell * 0.4, b.y - cell * 0.4, cell * 0.8, cell * 0.8); g.restore(); }
      if (tb.slide >= 0) this.slideTiles(L, tb.slide);
      lz.boxes.forEach((b, i) => {
        if (live) {
          const hit = UI._hit('ck-box' + bi + '_' + i, b.x, b.y, cell, cell);
          if (hit.hover && !this.marks.box[i] && i !== this.sel) { g.save(); g.globalAlpha *= 0.3; g.fillStyle = '#f7df8e'; g.fillRect(b.x - cell * 0.42, b.y - cell * 0.42, cell * 0.84, cell * 0.84); g.restore(); }
          if (hit.clicked) this.how(i);
        }
        if (UI.keys && !docked && spot) this.badge(String(i + 1), b.x - cell * 0.36, b.y - cell * 0.36, Math.round(cell * 0.12), PENCIL);
        const m = this.marks.box[i];
        if (m) { tick(b.x + cell * 0.3, b.y + cell * 0.3, cell / 180, PENCIL, m.at); }
      });
      // how the selected box was built: pencil arrows from its top letter and its side letter
      if (spot && this.sel >= 0) {
        const i = this.sel, b = lz.boxes[i], box = sq.boxes[i], tp = lz.top[box.col], sp = lz.side[box.row], pr = clamp((T - this.selAt) / 0.35);
        const w = docked ? 7 : 5;
        inkArrow(tp.x + cell * 0.12, tp.y + lz.tileSize * 0.6, b.x + cell * 0.06, b.y - cell * 0.28, { w, color: C.red, seed: 940 + i, prog: pr, head: 18, bulge: 10 });
        inkArrow(sp.x + lz.tileSize * 0.6, sp.y + cell * 0.1, b.x - cell * 0.3, b.y + cell * 0.02, { w, color: C.red, seed: 944 + i, prog: pr, head: 18, bulge: -10 });
        inkCircle(tp.x, tp.y, lz.tileSize * 0.72, { w: 4, color: C.red, seed: 948, prog: pr, passes: 1 });
        inkCircle(sp.x, sp.y, lz.tileSize * 0.72, { w: 4, color: C.red, seed: 949, prog: pr, passes: 1 });
      }
    }
    /* the honest tiles slide in from the parents' edges (the top letter and the side letter of that box) */
    slideTiles(L, k) {
      const Pl = PL(), P = this.P, lz = L.tray, cell = L.cell, i = P.answer.index, box = P.square.boxes[i], b = lz.boxes[i];
      const gt = box.genotype, ts = cell * 0.27, ty = b.y - cell * 0.17;
      const from = { top: lz.top[box.col], side: lz.side[box.row] };
      const src = [gt[0] === box.top ? 'top' : 'side'];
      src.push(src[0] === 'top' ? 'side' : 'top');
      const e = E.inOutCubic(k);
      [0, 1].forEach(j => {
        const f = from[src[j]], tx = b.x + (j ? 1 : -1) * ts * 0.56;
        const x = lerpN(f.x, tx, e), y = lerpN(f.y, ty, e) - Math.sin(e * Math.PI) * cell * 0.25;
        Pl.tile(gt[j], x, y, lerpN(lz.tileSize, ts, e), { seed: 960 + j, rot: (1 - e) * 0.2 * (j ? 1 : -1) });
      });
    }
    drawClaims(L, docked, live, spot) {
      const Pl = PL(), C = Pl.C, P = this.P, bi = this.bi;
      hand('The club says:', L.colX, L.colTop + L.head * 0.42, L.hs, -1e6, { align: 'left', color: C.leafDark, seed: 930 });
      L.rows.forEach(r => {
        const c = P.claims[r.i];
        if (live) {
          const hit = UI._hit('ck-claim' + bi + '_' + r.i, L.colX + L.colW / 2, r.cy, L.colW + 16, r.h);
          if (hit.hover && !this.marks.claim[r.i]) { g.save(); g.globalAlpha *= 0.28; g.fillStyle = '#f7df8e'; g.fillRect(L.colX - 8, r.top, L.colW + 16, r.h); g.restore(); }
          if (hit.clicked) this.tapClaim(r.i);
        }
        // the letter block (woodtype on kraft)
        const bs = L.bw * 0.86, by = r.top + L.pad + L.lh / 2;
        paperRect(L.colX + L.bw / 2, by, Rn(bs), Rn(bs), C.kraft, { seed: 950 + r.i, depth: 0.5, boil: false });
        g.save(); g.font = `${Math.round(bs * 0.62)}px ${SLAB}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(LETTER[r.i], L.colX + L.bw / 2, by + 1); g.restore();
        g.save(); g.font = L.font; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle';
        r.lines.forEach((l, j) => g.fillText(l, L.colX + L.bw + 20, r.top + L.pad + (j + 0.5) * L.lh));
        g.restore();
        const m = this.marks.claim[r.i];
        if (m) {
          const a = clamp((T - m.at) / 0.25);
          g.save(); g.globalAlpha *= 0.5 * a; g.fillStyle = C.cream; g.fillRect(L.colX - 6, r.top + 2, L.colW + 12, r.h - 4); g.restore();
          tick(L.colX + L.bw / 2, by, L.fs / 40, PENCIL, m.at);
          hand('checks out', L.colX + L.colW - 10, r.top + r.h - L.fs * 0.35, Math.round(L.fs * 0.9), m.at + 0.1, { align: 'right', color: PENCIL, rot: -0.04, seed: 955 + r.i, dur: 0.3 });
        }
      });
    }
    circleFound(L) {
      const C = PL().C, f = this.found, at = this.foundAt, pr = clamp((T - at) / 0.45);
      if (f.kind === 'box') { const b = L.tray.boxes[f.index]; inkCircle(b.x, b.y, L.cell * 0.5, { w: L.mode === 'd' ? 9 : 7, color: C.red, seed: 970, prog: pr, passes: 2 }); return; }
      const r = L.rows[f.index], w = Math.max(...r.lineW) + L.bw + 20;
      loop(L.colX + w / 2 - 4, r.cy, Math.min(w / 2 + 22, L.colW / 2 + 26), r.h / 2 + 6, { w: L.mode === 'd' ? 8 : 5, color: C.red, seed: 972, prog: pr });
    }
    /* Mr. Mendoza's red pencil: strike, the honest tiles, his note on a slip; an all-correct poster gets ticks + a stamp */
    drawMarks(L) {
      const Pl = PL(), C = Pl.C, P = this.P, t0 = this.fixAt + 0.2, d = L.mode === 'd';
      let noteAt;
      if (P.answer.kind === 'claim') {
        const r = L.rows[P.answer.index];
        r.lines.forEach((l, j) => {
          const x0 = L.colX + L.bw + 12, x1 = x0 + r.lineW[j] + 16, y = r.top + L.pad + (j + 0.5) * L.lh, a = t0 + j * 0.3, pr = clamp((T - a) / 0.3);
          ink([[x0, y + 5], [x1, y - 5]], { w: d ? 8 : 6, color: C.red, seed: 980 + j, prog: pr, passes: 2, amp: 1.2 });
          if (pr > 0 && pr < 1) pencil(lerpN(x0, x1, pr), lerpN(y + 5, y - 5, pr), d ? 1.3 : 1);
        });
        noteAt = t0 + r.lines.length * 0.3 + 0.1;
      } else if (P.answer.kind === 'box') {
        const i = P.answer.index, b = L.tray.boxes[i], cell = L.cell, ts = cell * 0.27, y = b.y - cell * 0.17, pr = clamp((T - t0 - 0.35) / 0.3);
        if (T - t0 < 0.9) {
          const x0 = b.x - ts * 1.3, x1 = b.x + ts * 1.3;
          ink([[x0, y + 12], [x1, y - 12]], { w: d ? 9 : 7, color: C.red, seed: 985, prog: pr, passes: 2 });
          if (pr > 0 && pr < 1) pencil(lerpN(x0, x1, pr), lerpN(y + 12, y - 12, pr), d ? 1.3 : 1);
        }
        noteAt = t0 + 1.8;
      } else {
        L.rows.forEach((r, k) => tick(L.colX - 22, r.top + L.pad + L.lh / 2, L.fs / 40, C.leafDark, t0 + 0.2 + k * 0.15));
        const below = L.fixTop + L.fixH + (d ? 130 : 110), room = below + (d ? 80 : 70) <= L.colBot;
        const s = pop(T, t0 + 0.6, 0.3) * (room ? (d ? 0.8 : 0.7) : (d ? 0.62 : 0.5));
        UI.stamp('ALL CORRECT', room ? L.colX + L.colW / 2 : L.tray.x, room ? below : L.tray.y + L.cell * 0.05, { rot: -0.1, s, color: C.leafDark });
        noteAt = t0 + 0.9;
      }
      this.drawNote(L, noteAt, P.answer.kind === 'none' ? C.leafDark : C.red);
    }
    drawNote(L, t0, color) {
      if (T < t0) return;
      const C = PL().C, x = L.colX, w = L.colW, y = L.fixTop, h = L.fixH, sc = pop(T, t0, 0.25, 1.6);
      g.save(); g.translate(x + w / 2, y + h / 2); g.rotate(-0.012); g.scale(sc, sc);
      paperRect(0, 0, R10(w), Math.round(h / 4) * 4, '#fbf5e6', { seed: 990, depth: 0.8, boil: false });
      g.restore();
      L.fxLines.forEach((l, j) => hand(l, x + 26, y + 14 + (j + 0.5) * L.fxLh, L.fxs, t0 + 0.15 + j * 0.35, { align: 'left', color, rot: -0.012, seed: 991 + j, dur: 0.35 }));
      hand('— Mr. Mendoza', x + w - 24, y + h - L.fxs * 0.7, Math.round(L.fxs * 0.8), t0 + 0.2 + L.fxLines.length * 0.35, { align: 'right', color, seed: 999, dur: 0.3 });
      void C;
    }
    badge(txt, x, y, r, color) {
      g.save(); g.fillStyle = color; g.beginPath(); g.arc(x, y, r, 0, TAU); g.fill();
      g.font = `${Math.round(r * 1.2)}px ${SLAB}`; g.fillStyle = '#fbf5e6'; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, x, y + 1); g.restore();
    }

    /* ---------------- the right-hand panel (desktop / landscape) ---------------- */
    panelTop() { const hs = UI.hudSlot(0); return Math.max(150, hs.y + hs.r + 22); }
    card(x, y, w, h, seed) { paperRect(x + w / 2, y + h / 2, R10(w), R10(h), PL().C.cream, { seed, depth: 1.3, boil: false }); }
    spotCard() {
      const key = this.bi + '|' + this.sel + '|' + this.misses + '|' + Math.round(UI.text(28)) + '|' + !!this.marks.box[this.sel];
      if (this._sc && this._sc.key === key) return this._sc;
      const X = 1335, W = 1886 - X, top0 = this.panelTop(), P = this.P;
      let S;
      for (const k of [1, 0.9, 0.8, 0.72, 0.64]) {
        const qs = Math.round(UI.text(40) * k), bs = Math.round(UI.text(28) * k), hs = Math.round(UI.text(30) * k), ns = Math.round(UI.text(30) * k);
        const qL = UI._wrap(`Poster ${this.bi + 1} of ${this.n}`, W - 60, `${qs}px ${SLAB}`);
        const body = UI._wrap('Tap the wrong box or the wrong claim. Nothing wrong? All correct.', W - 60, `${bs}px ${SERIF}`);
        const sel = this.sel >= 0;
        const hint = UI._wrap(sel ? `The ${BOXN[this.sel]} box: ${build(P, this.sel)}.` : 'Tap a box to see how it was built (free).', W - 60, `${hs}px ${HAND}`);
        const markB = sel && !this.marks.box[this.sel];
        const bh = UI.tap(Math.round(84 * Math.max(k, 0.8)));
        const sayL = this.say ? UI._wrap(this.say.text, W - 60, `${ns}px ${HAND}`) : [];
        const h = 28 + qL.length * qs * 1.15 + 12 + body.length * bs * 1.3 + 14 + hint.length * hs * 1.15 + (markB ? 18 + bh : 0) + 26;
        const keyL = UI.keys ? UI._wrap('Keys: 1–4 look at a box · X mark it · A–C claim · 0 all correct', W - 40, `${Math.round(hs * 0.8)}px ${HAND}`) : [];
        const total = h + 26 + bh + keyL.length * hs + 10 + (sayL.length ? 24 + sayL.length * ns * 1.15 + 30 : 0);
        S = { key, X, W, qs, bs, hs, ns, qL, body, hint, sel, markB, bh, sayL, keyL, h, top: top0 };
        if (top0 + total <= 1070) break;
      }
      return (this._sc = S);
    }
    drawSpot() {
      const S = this.spotCard(), C = PL().C, { X, W, qs, bs, hs, ns, qL, body, hint, sel, markB, bh, sayL, keyL, h, top } = S;
      this.card(X, top, W, h, 820);
      let y = top + 28;
      text(qL, X + 30, y, qs * 1.15, `${qs}px ${SLAB}`, C.leafDark); y += qL.length * qs * 1.15 + 12;
      text(body, X + 30, y, bs * 1.3, `${bs}px ${SERIF}`, C.ink); y += body.length * bs * 1.3 + 14;
      hint.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * hs * 1.15, hs, sel ? this.selAt : this.boardAt + 0.3, { align: 'left', color: sel ? C.red : PENCIL, seed: 824 + j, dur: 0.3 }));
      y += hint.length * hs * 1.15;
      if (markB) {
        y += 18;
        if (UI.button('ck-mark' + this.bi + '_' + this.sel, 'Mark this box wrong', X + W / 2, y + bh / 2, { w: Math.min(W - 50, 460), h: 84, color: C.red, size: 40, font: HAND })) this.tapBox(this.sel);
      }
      const by = top + h + 26 + bh / 2;
      if (this.t > 0.2 && UI.button('ck-legit' + this.bi, 'All correct', X + W / 2, by, { w: Math.min(W - 50, 420), h: 84, color: C.leafDark, size: 44, font: HAND })) this.legit();
      let ky = by + bh / 2 + 6;
      keyL.forEach(l => { ky += hs * 0.9; hand(l, X + W / 2, ky, Math.round(hs * 0.8), this.boardAt + 0.5, { color: PENCIL, seed: 829, dur: 0.01 }); });
      this.panelBottom = Math.max(ky + 10, by + bh / 2);
      if (sayL.length) {
        const ny = ky + 24, nh = sayL.length * ns * 1.15 + 30, sc = pop(T, this.say.at, 0.25, 1.6);
        if (sc > 0) { g.save(); g.translate(X + W / 2, ny + nh / 2); g.rotate(0.01); g.scale(sc, sc); paperRect(0, 0, R10(W), Math.round(nh / 6) * 6, '#fbf5e6', { seed: 831 + sayL.length, depth: 0.9, boil: false }); g.restore(); }
        sayL.forEach((l, j) => hand(l, X + 30, ny + 15 + (j + 0.5) * ns * 1.15, ns, this.say.at + 0.1 + j * 0.3, { align: 'left', color: C.red, seed: 832 + j, dur: 0.3 }));
        this.panelBottom = ny + nh;
      }
    }
    /* the question card: the options as paper rows; it moves left over the poster when big type doesn't fit */
    qCard() {
      const key = this.bi + '|' + this.picks.length + '|' + Math.round(UI.text(28));
      if (this._qc && this._qc.key === key) return this._qc;
      const q = this.inst, top0 = this.panelTop(), f = this.found;
      const intro = this.revealed ? (f.kind === 'legit' ? 'It was all correct.' : 'Mr. Mendoza circled it.') : f.kind === 'legit' ? 'Right: all correct!' : f.kind === 'box' ? 'Got it: that box is wrong.' : 'Got it: that claim is wrong.';
      const last = this.picks[this.picks.length - 1], why = last != null && last !== q.answerIndex ? q.why[last] : '';
      let R;
      outer: for (const X of [1335, 1180, 1000, 820]) {
        for (const k of [1, 0.9, 0.8]) {
          const W = 1886 - X;
          const is = Math.round(UI.text(32) * k), qs = Math.round(UI.text(34) * k), os = Math.round(UI.text(28) * k), ws = Math.round(UI.text(30) * k);
          const iL = UI._wrap(intro, W - 60, `${is}px ${HAND}`);
          const qL = UI._wrap(q.text, W - 60, `${qs}px ${SLAB}`);
          const opts = q.options.map(o => UI._wrap(o, W - 130, `${os}px ${SERIF}`));
          const rows = opts.map(ls => Math.max(UI.tap(80), ls.length * os * 1.25 + 24));
          const whyL = why ? UI._wrap(why, W - 60, `${ws}px ${HAND}`) : [];
          const h = 24 + iL.length * is * 1.15 + 8 + qL.length * qs * 1.18 + 14 + rows.reduce((a, b) => a + b, 0) + 14 + whyL.length * ws * 1.15 + (whyL.length ? 16 : 0);
          const top = Math.max(96, Math.min(top0, 1070 - h));
          R = { key, X, W, is, iL, qs, qL, os, opts, rows, ws, whyL, h, top };
          if (top0 + h <= 1070) break outer;
        }
      }
      return (this._qc = R);
    }
    drawQ() {
      const R = this.qCard(), C = PL().C, q = this.inst, t0 = this.qAt, { X, W, is, iL, qs, qL, os, opts, rows, ws, whyL, h, top } = R;
      if (T < t0) return;
      this.card(X, top, W, h, 850);
      let y = top + 24;
      iL.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * is * 1.15, is, t0, { align: 'left', color: this.found.kind === 'legit' ? C.leafDark : C.red, seed: 852 + j, dur: 0.3 }));
      y += iL.length * is * 1.15 + 8;
      text(qL, X + 30, y, qs * 1.18, `${qs}px ${SLAB}`, C.leafDark); y += qL.length * qs * 1.18 + 14;
      opts.forEach((ls, i) => {
        const rh = rows[i], cy = y + rh / 2, crossed = this.picks.includes(i);
        const hit = crossed ? { hover: false, clicked: false } : UI._hit('ck-opt' + this.bi + '_' + i, X + W / 2, cy, W - 30, rh - 6);
        paperRect(X + W / 2, cy, R10(W - 40), Math.round((rh - 10) / 2) * 2, hit.hover ? '#fffaf0' : crossed ? '#e6dcc6' : C.label, { seed: 860 + i, depth: hit.hover ? 1.4 : 0.6, boil: false });
        g.save(); g.fillStyle = crossed ? PENCIL : C.leafDark; g.fillRect(X + 32, cy - 22, 44, 44);
        g.font = `26px ${SLAB}`; g.fillStyle = C.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), X + 54, cy + 1); g.restore();
        text(ls, X + 96, cy - ls.length * os * 1.25 / 2, os * 1.25, `${os}px ${SERIF}`, crossed ? PENCIL : C.ink);
        if (crossed) {
          const mw = Math.max(...ls.map(l => measure(l, `${os}px ${SERIF}`))), a = i === this.picks[this.picks.length - 1] ? this.pickedAt : -1e9;
          ink([[X + 90, cy + 10], [X + 104 + mw, cy - 10]], { w: 5, color: C.red, seed: 880 + i, prog: clamp((T - a) / 0.3), passes: 1 });
        }
        if (hit.clicked) this.choose(i);
        y += rh;
      });
      y += 14;
      whyL.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * ws * 1.15, ws, this.pickedAt + 0.2 + j * 0.2, { align: 'left', color: C.red, seed: 890 + j }));
      this.panelBottom = X < 1335 ? 1080 : top + h;
    }
    /* Mr. Mendoza's card: the verdict, the kind of mistake, the stars */
    fixLines() {
      const P = this.P, r = this.results[this.bi] || { stars: 0, spotStar: 0, fixStar: 0 }, out = [], legit = P.answer.kind === 'none';
      out.push(['head', legit ? (r.spotStar ? 'All correct. Well checked!' : 'It was all correct.') : 'Mr. Mendoza\'s red pencil']);
      if (!legit) out.push(['rule', 'The mistake: ' + (SHORT[P.cheat] || P.cheat) + '.']);
      out.push(['rule', "Mr. Mendoza's note is on the poster."]);
      out.push(['stars', `+${r.stars} star${r.stars === 1 ? '' : 's'} (${legit ? 'all correct' : 'spot'} ${r.spotStar}, fix ${r.fixStar})`]);
      return out;
    }
    fixCard() {
      const key = this.bi + '|' + Math.round(UI.text(28));
      if (this._fc && this._fc.key === key) return this._fc;
      const F = this.fixLines(), top0 = this.panelTop(), bh = UI.tap(92), X = 1335, W = 1886 - X;
      let R;
      for (const k of [1, 0.9, 0.8, 0.7]) {
        const S = { head: [Math.round(UI.text(36) * k), SLAB], rule: [Math.round(UI.text(30) * k), SERIF], stars: [Math.round(UI.text(34) * k), HAND] };
        const rows = F.flatMap(([kk, t]) => UI._wrap(t, W - 64, `${S[kk][0]}px ${S[kk][1]}`).map(l => [kk, l]));
        const h = rows.reduce((a, [kk]) => a + S[kk][0] * 1.28, 0) + 50;
        R = { key, X, W, rows, h, S, bh, top: top0 };
        if (top0 + h + 26 + bh + 14 <= 1080) break;
      }
      return (this._fc = R);
    }
    drawFix() {
      const R = this.fixCard(), C = PL().C, { X, W, rows, h, S, bh, top } = R, t0 = this.fixAt + 0.2;
      this.card(X, top, W, h, 900);
      let y = top + 26, tt = t0;
      const col = { head: C.red, rule: C.ink, stars: C.leafDark };
      rows.forEach(([k, l]) => {
        const s = S[k][0], cy = y + s * 0.64;
        if (k === 'stars') hand(l, X + 32, cy, s, tt, { align: 'left', color: col[k], seed: 910 + Math.round(y) % 50, dur: 0.4 });
        else if (T >= tt) { g.save(); g.font = `${s}px ${S[k][1]}`; g.fillStyle = k === 'head' && this.P.answer.kind === 'none' ? C.leafDark : col[k]; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(l, X + 32, cy); g.restore(); }
        tt += 0.12; y += s * 1.28;
      });
      const label = this.bi < this.n - 1 ? 'Next poster' : 'See my stars';
      this.panelBottom = top + h + 26 + bh;
      if (Game.time - this.fixAt > NEXT_GUARD && UI.button('ck-next', label, X + W / 2, top + h + 26 + bh / 2, { w: 420, h: 92, color: C.leafDark, size: 46, font: HAND })) this.next();
    }

    /* ---------------- portrait: the poster is the picture; everything to tap is in the dock ---------------- */
    drawDock() {
      const P = this.P, q = this.inst, id = this.attempt + '_' + this.bi;
      const claimOpts = P.claims.map((c, i) => `${LETTER[i]}. ${c.text}`);
      if (this.phase === 'drop' || this.phase === 'spot') {
        Dock.text('ck-how', ['Find the mistake: tap the wrong claim, or check a box and mark it wrong. Nothing wrong? All correct.'], { cls: 'note' });
        if (this.phase !== 'spot') return;
        Dock.choices('ck-claims' + id, { prompt: 'The club says:', options: claimOpts, state: P.claims.map((c, i) => (this.marks.claim[i] ? 'faded' : '')),
          notes: P.claims.map((c, i) => (this.marks.claim[i] ? 'Checks out: ' + checksOut(P, c) : '')), onChoose: i => this.tapClaim(i) });
        Dock.text('ck-boxq' + id, ['The seed tray (tap a box to see how it was built, free):'], { cls: 'small' });
        Dock.buttons('ck-boxes' + id, P.shown.map((b, i) => ({ label: `${BOXN[i]}: ${b.genotype}${this.marks.box[i] ? ' (fine)' : ''}`, onClick: () => this.how(i) })));
        if (this.sel >= 0) {
          Dock.text('ck-build' + id + '_' + this.sel, [`The ${BOXN[this.sel]} box: ${build(P, this.sel)}.`], { cls: 'note', reveal: true });
          if (!this.marks.box[this.sel]) Dock.buttons('ck-mark' + id + '_' + this.sel, [{ label: `Mark the ${BOXN[this.sel]} box wrong`, wide: true, onClick: () => this.tapBox(this.sel) }]);
        }
        Dock.buttons('ck-legit' + id, [{ label: 'All correct', wide: true, onClick: () => this.legit() }]);
        if (this.say) Dock.text('ck-say' + id + '_' + this.misses, [this.say.text], { cls: 'wrong', reveal: true });
        return;
      }
      const f = this.found;
      const foundTxt = f.kind === 'legit' ? (this.revealed ? 'It was all correct.' : 'Right: all correct!')
        : (this.revealed ? 'Mr. Mendoza circled: ' : 'You circled: ') + (f.kind === 'box' ? `the ${BOXN[f.index]} box (${P.shown[f.index].genotype})` : claimOpts[f.index]);
      if (this.phase === 'q') {
        Dock.text('ck-found' + id, [foundTxt], { cls: this.revealed ? 'note' : 'right' });
        Dock.choices('ck-q' + id, { prompt: q.text, options: q.options, state: q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : '')),
          notes: q.options.map((o, i) => (this.picks.includes(i) ? q.why[i] : '')), onChoose: i => this.choose(i) });
        return;
      }
      const F = this.fixLines();
      Dock.choices('ck-q' + id, { prompt: q.text, options: q.options, state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.heading('ck-fixh' + id, F[0][1]);
      Dock.text('ck-fixt' + id, [...(P.answer.kind === 'none' ? [] : ['The mistake: ' + (SHORT[P.cheat] || P.cheat) + '.']), 'Mr. Mendoza: ' + (q.explain || '')], { cls: 'note' });
      Dock.text('ck-stars' + id, F.filter(([k]) => k === 'stars').map(([, t]) => t), { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next poster' : 'See my stars', label: 'next', aria: 'next poster', go: true, onClick: () => this.next() });
    }

    introLines() {
      return ['The garden club made a poster for every cross. Mr. Mendoza wants them checked before the show.',
        'Most posters have one mistake: a box filled in wrong, or a claim the square doesn\'t back up.',
        'Tap it, then pick the fix. Nothing wrong? Call it All correct.', `${this.n} posters. ${this.passStars} of ${this.of} stars for the stamp.`];
    }
    drawMendoza(x, y, h) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('mendoza', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: 0 });
    }
    drawIntro(docked) {
      const t0 = this.enteredAt, Pl = PL(), C = Pl.C, lines = this.introLines();
      this.drawMendoza(docked ? 1620 : 1640, 1080, docked ? 720 : 780);
      if (docked) {
        Pl.label('SEED CHECK', 760, 420, 96, { t0: t0 + 0.2, seed: 601 });
        Dock.heading('ck-intro', 'Seed Check', { cls: 'big' });
        Dock.text('ck-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start', label: 'start', aria: 'Start checking posters', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      Pl.label('SEED CHECK', 780, 270, 84, { t0: t0 + 0.2, seed: 601 });
      // the text shrinks until the card and the Start button fit (a phone's UI.text scale makes it wrap more)
      const W = 1040, top = 380, bh = UI.tap(110);
      let fs = Math.round(UI.text(36)), wl, nL, h;
      for (;;) {
        wl = lines.map((l, i) => UI._wrap(l, W - 70, `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`));
        nL = wl.reduce((a, l) => a + l.length, 0); h = nL * fs * 1.42 + 60;
        if (top + h + 24 + bh <= 1070 || fs <= 24) break;
        fs -= 2;
      }
      this.card(780 - W / 2, top, W, h, 935);
      let y = top + 30 + fs * 0.71;
      wl.forEach((ls, i) => ls.forEach(tl => {
        if (T >= t0 + 0.5 + i * 0.3) { g.save(); g.font = `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`; g.fillStyle = i === 3 ? C.red : C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.globalAlpha = clamp((T - t0 - 0.5 - i * 0.3) / 0.3); g.fillText(tl, 780, y); g.restore(); }
        y += fs * 1.42;
      }));
      const by = Math.min(1080 - bh / 2 - 10, top + h + 24 + bh / 2);
      if (this.t > 0.8 && UI.button('ck-start', 'Start', 780, by, { w: 420, h: 110, color: C.leafDark, size: 56, font: HAND })) this.begin();
      if (this.t > 1.2) hand(UI.hint('(or press Enter)', ''), 1080, by, UI.text(28), t0 + 1.2, { align: 'left', color: PENCIL, seed: 603 });
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons(), Pl = PL(), C = Pl.C;
      const rows = this.instances.map((inst, i) => { const r = this.results[i] || { stars: 0 }; const P0 = Cross.poster(inst.item); return { r, text: `${shortTitle(P0).toLowerCase()}: ${SHORT[inst.item.cheat] || inst.item.cheat}` }; });
      const word = this.passed ? 'CHECKED' : 'TRY AGAIN';
      if (docked) {
        Pl.woodtype(`${s} / ${of}`, 700, 380, 140, { t0: this.endAt + 0.1, seed: 950 });
        UI.stamp(word, 700, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.5, color: C.red });
        this.drawMendoza(1620, 1080, 700);
        Dock.heading('ck-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('ck-end', [this.passed ? 'Checked! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New posters?`]);
        Dock.list('ck-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? C.leafDark : C.red, lines: [x.text, `${x.r.stars} of 2 stars`] })));
        Dock.text('ck-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('ck-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Pl.woodtype(`${s} / ${of}`, 700, 250, 110, { t0: this.endAt + 0.1, seed: 950 });
      UI.stamp(word, 1320, 250, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1, color: C.red });
      const fs = Math.round(UI.text(30)), rh = Math.max(62, fs * 1.5), bh = UI.tap(100), ch = (rows.length + 1) * rh + 50;
      const cardTop = Math.min(350, 1080 - 40 - bh - 36 - ch), X0 = 220, W = 1480;
      this.card(X0, cardTop, W, ch, 960);
      rows.forEach((x, i) => {
        const y = cardTop + 25 + (i + 0.5) * rh;
        let t = `${i + 1}. ${x.text}`;
        g.save(); g.font = `${fs}px ${SERIF}`;
        if (g.measureText(t).width > W - 330) { while (g.measureText(t + '…').width > W - 330 && t.length > 8) t = t.slice(0, -1); t = t.trimEnd() + '…'; }
        g.fillStyle = x.r.legit ? C.leafDark : C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(t, X0 + 50, y); g.restore();
        hand(`${x.r.stars} of 2`, X0 + W - 60, y, fs * 1.2, this.endAt + 0.4 + i * 0.1, { align: 'right', color: x.r.stars === 2 ? C.leafDark : C.red, seed: 970 + i });
      });
      const py = cardTop + 25 + (rows.length + 0.5) * rh, by = Math.min(1080 - 40 - bh / 2, cardTop + ch + 36 + bh / 2);
      hand(this.passed ? 'Checked! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New posters?`, X0 + W / 2, py, Math.round(UI.text(40)), this.endAt + 0.8, { color: C.red, seed: 980, dur: 0.5 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('ck-end' + i, b.label, 700 + i * 520, by, { w: 480, h: 100, color: i === 0 ? C.leafDark : C.woodDark, size: 44, font: HAND })) b.go(); });
      hand(`quiz code ${this.seed}`, 1880, by + bh / 2 + 10 > 1070 ? by - bh / 2 - 20 : by + bh / 2 + 10, 28, this.endAt + 0.6, { align: 'right', color: PENCIL, seed: 985 });
    }
  }

  /* small drawing helpers */
  function text(lines, x, yTop, lh, font, color) {
    g.save(); g.font = font; g.fillStyle = color; g.textAlign = 'left'; g.textBaseline = 'middle';
    lines.forEach((l, j) => g.fillText(l, x, yTop + (j + 0.5) * lh));
    g.restore();
  }
  function loop(cx, cy, rx, ry, { w = 5, color, seed = 1, prog = 1 } = {}) {
    const pts = [], n = 64;
    for (let i = 0; i <= n * 1.08; i++) { const a = i / n * TAU - 2.2, c = Math.cos(a), s = Math.sin(a); pts.push([cx + rx * Math.sign(c) * Math.pow(Math.abs(c), 0.45) * (1 + 0.02 * Math.sin(i * 0.7)), cy + ry * Math.sign(s) * Math.pow(Math.abs(s), 0.6)]); }
    ink(pts, { w, color, seed, prog, passes: 1, step: 12, amp: 1.4 });
  }
  function tick(x, y, s, color, t0) {
    const pr = clamp((T - t0) / 0.3);
    if (pr <= 0) return;
    ink([[x - 18 * s, y], [x - 4 * s, y + 16 * s], [x + 24 * s, y - 22 * s]], { w: Math.max(4, 6 * s), color, seed: Math.round(x + y) % 97 + 1, prog: pr, passes: 1, step: 6 });
  }
  /* Mr. Mendoza's red pencil (its tip at x, y), drawn with plain fills: cheap, no shadows */
  function pencil(x, y, s = 1) {
    g.save(); g.translate(x, y); g.rotate(-0.75); g.scale(s, s);
    g.fillStyle = '#2b2420'; g.beginPath(); g.moveTo(0, 0); g.lineTo(8, -5); g.lineTo(8, 5); g.closePath(); g.fill();
    g.fillStyle = '#e8c89a'; g.beginPath(); g.moveTo(8, -5); g.lineTo(30, -11); g.lineTo(30, 11); g.lineTo(8, 5); g.closePath(); g.fill();
    g.fillStyle = '#b83a2b'; g.fillRect(30, -11, 130, 22);
    g.fillStyle = 'rgba(0,0,0,0.18)'; g.fillRect(30, 3, 130, 8);
    g.fillStyle = '#c9c1b1'; g.fillRect(160, -11, 14, 22);
    g.fillStyle = '#d98a8a'; g.fillRect(174, -11, 16, 22);
    g.restore();
  }

  Check.STEP = STEP; Check.pool = pool; Check.layout = posterLay; Check.checksOut = checksOut;
  Object.defineProperty(Check, 'solution', { get() { const s = Game.current(); return s instanceof Check ? s.solution : null; } });
  /* the live scene's tap rects on the canvas (tests click them): the 4 boxes and the claims, centred */
  Check.zones = () => {
    const s = Game.current();
    if (!(s instanceof Check) || !s.P || s.phase === 'intro' || s.phase === 'end') return null;
    const L = s.lay(UI.docked());
    return { boxes: L.tray.boxes.map(b => ({ i: b.i, x: b.x, y: b.y, w: L.cell, h: L.cell })), claims: L.rows.map(r => ({ i: r.i, x: L.colX + L.colW / 2, y: r.cy, w: L.colW, h: r.h })) };
  };
  /* tests / screenshots: swap the current poster for a pool item (by id), back to the spot phase */
  Check.preview = id => {
    const s = Game.current();
    if (!(s instanceof Check) || !s.instances) return false;
    const it = pool().items.find(x => x.id === id);
    if (!it) return false;
    const inst = Bank.draw({ ...pool(), items: [it], draw: { ...pool().draw, n: 1, require: [], unique: null } }, { seed: 7 })[0];
    s.instances[s.bi] = inst;
    s.setup(s.bi); s.boardAt = Game.time - DROP - 2; s.phase = 'spot';
    return true;
  };
  Levels.check = Check;
})();
